import { useEffect, useState } from 'react';
import { myRequests, api } from '@/api';
import { Eye, Trash2, Phone, Users, Clock, Plus, ArrowLeft, MapPin } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';

function formatDate(iso){
  try{
    const d = new Date(iso);
    const mesi = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
    return `${d.getDate()} ${mesi[d.getMonth()]} ${d.getFullYear()}`;
  }catch{return iso||''}
}

const STATUS_CONFIG = {
  open: { label: 'Pubblicata', color: 'bg-blue-100 text-blue-700' },
  assigned: { label: 'Contattato', color: 'bg-amber-100 text-amber-700' },
  closed: { label: 'Chiusa', color: 'bg-green-100 text-green-700' },
  canceled: { label: 'Annullata', color: 'bg-slate-100 text-slate-600' },
};

export default function ClientRequests(){
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const nav = useNavigate();

  const loadRequests = () => {
    myRequests()
      .then(r => {
        const sorted = (r.data || []).sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        setItems(sorted);
      })
      .catch(err => {
        console.error('Error loading requests:', err);
        setItems([]);
      })
      .finally(() => setLoaded(true));
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const openContacts = async (request) => {
    setSelectedRequest(request);
    setContactsDialogOpen(true);
    setLoadingContacts(true);
    
    try {
      const res = await api.get(`/requests/${request.id}/contacts`);
      setContacts(res.data.contacts || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
      setContacts([]);
    }
    
    setLoadingContacts(false);
  };

  const openDeleteDialog = (request) => {
    setRequestToDelete(request);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!requestToDelete) return;
    
    try {
      await api.delete(`/requests/${requestToDelete.id}`);
      toast.success('Richiesta eliminata');
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
      loadRequests();
    } catch (err) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => nav('/cliente/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <Link to="/">
                <img src={LOGO_URL} alt="ServiceGo" className="h-10 w-auto" />
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <Button 
                onClick={() => nav('/cliente/nuova')}
                className="rounded-xl bg-[#5848FF] hover:bg-[#4638EF] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuova
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Le mie Richieste</h1>
            <p className="text-slate-500 mt-1">{items.length} richieste pubblicate</p>
          </div>
        </div>
        
        {!loaded ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-slate-500">Caricamento...</div>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Nessuna richiesta</h3>
            <p className="text-slate-500 mb-6">Non hai ancora pubblicato nessuna richiesta</p>
            <Button 
              onClick={() => nav('/cliente/nuova')}
              className="rounded-xl bg-[#5848FF] hover:bg-[#4638EF] text-white"
            >
              Pubblica la tua prima richiesta
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(request => {
              const status = STATUS_CONFIG[request.status] || STATUS_CONFIG.open;
              
              return (
                <div key={request.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  <div className="p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3 className="font-semibold text-slate-900 truncate flex-1">
                        {request.title || 'Richiesta'}
                      </h3>
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    
                    {/* Description */}
                    <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                      {request.description}
                    </p>
                    
                    {/* Location */}
                    {request.address?.city && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-4">
                        <MapPin className="w-4 h-4" />
                        <span>{request.address.city}</span>
                      </div>
                    )}
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Eye className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">{request.viewsCount || 0}</div>
                          <div className="text-[10px] text-slate-500">Views</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                          <Phone className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">{request.contactsCount || 0}</div>
                          <div className="text-[10px] text-slate-500">Contatti</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Date */}
                    <p className="text-xs text-slate-400 mb-4">
                      Pubblicata il {formatDate(request.createdAt)}
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 rounded-lg text-[13px]"
                      onClick={() => openContacts(request)}
                    >
                      <Users className="w-4 h-4 mr-1.5" />
                      Chi mi ha contattato
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-lg w-9 h-9 p-0 border-rose-200 text-rose-500 hover:bg-rose-50"
                      onClick={() => openDeleteDialog(request)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Contacts Dialog */}
      <Dialog open={contactsDialogOpen} onOpenChange={setContactsDialogOpen}>
        <DialogContent className="rounded-2xl bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>{t('ui.whoContactedYou')}</DialogTitle>
          </DialogHeader>
          
          {loadingContacts ? (
            <div className="py-8 text-center text-slate-500">Caricamento...</div>
          ) : contacts.length === 0 ? (
            <div className="py-8 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-slate-500 mb-2">Nessun contatto ricevuto</p>
              <p className="text-sm text-slate-400">
                Le ditte interessate ti contatteranno sul recapito che hai fornito.
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {contacts.map((contact, idx) => (
                <div key={contact.id || idx} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">{contact.companyName || 'Ditta'}</div>
                      <div className="text-sm text-slate-500 mt-1">
                        {contact.contactType === 'whatsapp' && '📱 WhatsApp'}
                        {contact.contactType === 'call' && '📞 Chiamata'}
                        {contact.contactType === 'email' && '✉️ Email'}
                      </div>
                    </div>
                    <div className="text-xs text-slate-400">
                      {formatDate(contact.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="rounded-2xl bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('ui.deleteRequest')}</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Sei sicuro di voler eliminare questa richiesta? L'azione non può essere annullata.
          </p>
          <div className="flex gap-3 mt-4">
            <Button 
              variant="outline" 
              className="flex-1 rounded-xl"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Annulla
            </Button>
            <Button 
              className="flex-1 rounded-xl bg-rose-500 hover:bg-rose-600 text-white"
              onClick={confirmDelete}
            >
              Elimina
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
